import {
  TeButton,
  TeContainerSlim,
  TeText,
  TeFilter,
  TeScroller,
  TePopover,
  TeContainer,
  TeContextMenu,
  TeContextMenuControl,
  TeTag,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Fragment, useMemo } from 'react';
import { toStrArray } from '@topcon/topcon-utils';

import { Filter, FilterIDs } from '../../providers/filter/filter.types';
import { useFilterService } from '../../providers/filter/filter.context';
import { getSubtextForFilter } from '../../providers/filter/filter.utils';
import { border } from '../../providers/theme/theme.index';
import { useAppService } from '../../providers/app/app.context';
import { buildProfileMap, resolveProfileDisplayName } from '../../utils/profiles';

interface Props {
  filterID: FilterIDs;
  filters: Filter[];
  countText: string;
  actions: React.ReactNode;
  actionsDisabled: boolean;
  warningText?: string;
  warningSubText?: string;
}

function TableHeader({
  filters,
  filterID,
  countText,
  actions,
  actionsDisabled,
  warningText,
  warningSubText,
}: Props) {
  const { t } = useTranslation('users');
  const filterService = useFilterService();
  const { availableProfiles } = useAppService();
  const profileMap = useMemo(() => buildProfileMap(availableProfiles), [availableProfiles]);
  const getProfileName = (profileId: string | number) => {
    const profile = profileMap.get(`${profileId}`);
    return profile ? resolveProfileDisplayName(profile, t) : `${profileId}`;
  };

  const handleToggleFilter = (name: string) => {
    filterService.toggleFilter(filterID, name);
  };

  const handleRemoveFilter = (name: string) => {
    filterService.removeFilter(filterID, name);
  };

  return (
    <TeContainerSlim alignItems="center" gap="m" justifyContent="flex-start">
      <TeContainerSlim gap="s" alignItems="center">
        <TeContextMenuControl contextId="actions-menu">
          <TeButton
            size="s"
            text={t('actions')}
            postfixIcon="expand_more"
            disabled={actionsDisabled}
            blockInteraction={actionsDisabled}
            variant="neutral"
          />
        </TeContextMenuControl>
        <TeContextMenu contextId="actions-menu" size="s">
          {actions}
        </TeContextMenu>
        <TeText size={12}>{countText}</TeText>

        {warningText && (
          <>
            <TeTag
              text={t('warning')}
              popkey="limitReachedTag"
              backgroundColor="background.warning.quaternary"
              icon="warning"
            />
            <TePopover
              for="limitReachedTag"
              variant="secondary"
              operation="hover"
              placement="bottom"
            >
              <TeContainerSlim padding={['xs', 'm']} alignItems="center" column>
                <TeText color="text.inverse">{warningText}</TeText>
                <TeText color="text.inverse">{warningSubText}</TeText>
              </TeContainerSlim>
            </TePopover>
          </>
        )}
      </TeContainerSlim>

      {filters.length > 0 && (
        <TeContainerSlim grow overflow="hidden" gap="m" alignItems="center">
          <Divider />
          <TeScroller gap="xs">
            {filters.map((filter) => {
              const { active, name, value } = filter;
              const [firstVal] = toStrArray(value);
              // TODO: Figure out some cleaner way to handle conditional formatting of the data
              const optionText = firstVal;
              const isPlural = Array.isArray(value) && value.length > 1;
              const isStatus = name === 'status';
              const isProfile = name === 'profile';
              const subText: string | undefined = isPlural
                ? undefined
                : isStatus
                  ? t(optionText)
                  : isProfile
                    ? getProfileName(optionText)
                    : getSubtextForFilter(filter, optionText);
              const popoverText = isPlural
                ? isStatus
                  ? value.map((s: string | number) => t(`${s}`)).join(', ')
                  : isProfile
                    ? value.map((s: string | number) => getProfileName(s)).join(', ')
                    : value.join(', ')
                : undefined;
              const count = isPlural ? value.length : undefined;
              const textValue = t(name);
              return (
                <Fragment key={name}>
                  <TeFilter
                    text={textValue}
                    subtext={subText}
                    count={count}
                    active={active}
                    teclick={() => handleToggleFilter(name)}
                    teclose={() => handleRemoveFilter(name)}
                  >
                    {isPlural && (
                      <TePopover operation="hover" variant="secondary" placement="bottom">
                        <TeContainer padding={['xs', 'm']}>
                          <TeText color="text.inverse">{popoverText}</TeText>
                        </TeContainer>
                      </TePopover>
                    )}
                  </TeFilter>
                </Fragment>
              );
            })}
          </TeScroller>
        </TeContainerSlim>
      )}
    </TeContainerSlim>
  );
}

export default TableHeader;

const Divider = styled.div`
  height: 16px;
  width: 1px;
  background: ${border.primary};
`;
